import type { GenerationRequestStatus } from '../../types/api';

/**
 * The history screen's status filter: four groups over the twelve statuses,
 * plus "all". The key is what lives in the URL (`/history?status=waiting`),
 * the statuses are what the list call sends as repeated `status` params.
 *
 * Running work has no chip of its own: it is minutes long, and the review
 * screen is where it is watched. It shows under All.
 */
export type HistoryFilterKey = 'all' | 'waiting' | 'done' | 'failed' | 'rejected';

export interface HistoryFilter {
  key: HistoryFilterKey;
  label: string;
  /** Empty for `all`: send no filter at all. */
  statuses: readonly GenerationRequestStatus[];
}

/** No filter: every state. Also what an unknown `?status=` falls back to. */
export const ALL_HISTORY: HistoryFilter = { key: 'all', label: 'All', statuses: [] };

export const HISTORY_FILTERS: readonly HistoryFilter[] = [
  ALL_HISTORY,
  { key: 'waiting', label: 'Waiting', statuses: ['awaiting_review', 'awaiting_final_review'] },
  { key: 'done', label: 'Done', statuses: ['complete'] },
  { key: 'failed', label: 'Failed', statuses: ['failed'] },
  { key: 'rejected', label: 'Rejected', statuses: ['rejected'] },
];

/** The URL parameter the filter lives in. */
export const HISTORY_STATUS_PARAM = 'status';

/** Where Home's "See all" lands: everything waiting on the user. */
export const HISTORY_WAITING_PATH = `/history?${HISTORY_STATUS_PARAM}=waiting`;

/**
 * The filter a `?status=` value names. The URL is user-editable, so anything
 * unknown — or nothing — is All rather than an error.
 */
export function historyFilterFor(value: string | null): HistoryFilter {
  return HISTORY_FILTERS.find((filter) => filter.key === value) ?? ALL_HISTORY;
}
